import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { RealEstateService, TenantUnitDetail, TenantUnitService } from '@eadsysteme/ewsrealestateclient-v2';
import {
	ConsumptionInfoReceiver,
	ConsumptionInfoReceiverService,
	Owner,
	OwnerService,
	RealEstate,
	Resident,
	TenantUnit,
	TenantUnitService as TenantUnitServiceV1,
} from '@eadsysteme/ewsrealestateclient-v1';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { Icons } from '../../util/Icons';
import { UntypedFormControl } from '@angular/forms';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { SnackbarService } from '../../services/snackbar.service';
import { LayoutService } from '../../../@vex/services/layout.service';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { TenantUnitDetailSettingsLegacyComponent } from './tenant-unit-detail-settings-legacy/tenant-unit-detail-settings-legacy.component';
import { EnergyEfficiencySettingService, RealEstateSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { Billing, BillingCustomCService, TenantUnitReference, TenantUnitReferenceService } from '@eadsysteme/billingclient-v1';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../util/material-dayjs-adapter';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { MatIcon } from '@angular/material/icon';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { TenantDetailCardsComponent } from '../tenant-detail-cards/tenant-detail-cards.component';
import { MatButton } from '@angular/material/button';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { RolesViewComponent } from './roles-view/roles-view.component';
import { TenantShareValueComponent } from './tenant-shares/tenant-share-value/tenant-share-value.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TenantUnitReferenceComponent } from '../billings/billing/tenantunit/billing-instances-master/billing-instance/tenant-unit-reference.component';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

dayjs.extend(utc);

@Component({
	selector: 'ara-tenant-unit-detail-legacy',
	templateUrl: './tenant-unit-detail-legacy.component.html',
	styleUrls: ['./tenant-unit-detail-legacy.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		MatIcon,
		MatTooltip,
		BreadcrumbsComponent,
		PageLayoutContentDirective,
		TenantDetailCardsComponent,
		MatButton,
		MatTabGroup,
		MatTab,
		RolesViewComponent,
		TenantShareValueComponent,
		MatProgressSpinner,
		TenantUnitReferenceComponent,
	],
})
export class TenantUnitDetailLegacyComponent implements OnInit, AfterViewInit {
	private readonly dialog = inject(MatDialog);
	private readonly sessionService = inject(SessionService);
	protected readonly layoutService = inject(LayoutService);
	private readonly router = inject(Router);
	private readonly translocoService = inject(TranslocoService);
	protected readonly icons = inject(Icons);
	private readonly realEstateService = inject(RealEstateService);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly tenantUnitServiceV1 = inject(TenantUnitServiceV1);
	private readonly snackBar = inject(SnackbarService);
	private readonly ownerService = inject(OwnerService);
	private readonly consumptionInfoReceiverService = inject(ConsumptionInfoReceiverService);
	private readonly activatedRoute = inject(ActivatedRoute);
	protected readonly breadCrumbService = inject(BreadCrumbService);

	private readonly destroyRef = inject(DestroyRef);
	private readonly energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	private readonly billingService = inject(BillingCustomCService);
	private readonly tenantUnitReferenceService = inject(TenantUnitReferenceService);

	selectedRealestate: WritableSignal<RealEstate | null> = signal(undefined);
	selectedTenantUnit: WritableSignal<TenantUnitDetail | null> = signal(undefined);
	tenantUnits: WritableSignal<TenantUnitDetail[]> = signal([]);
	billingUnit = signal<Billing>(undefined);
	tenantUnitReferences: WritableSignal<TenantUnitReference[]> = signal([]);
	selectedTenantUnitReference: WritableSignal<TenantUnitReference> = signal(undefined);

	finishedLoading = signal(false);
	showOwner = signal(false);
	showTenant = signal(false);
	showResident = signal(false);
	showConsumptionInfoReceiver = signal(false);

	selected = [];

	rolesSelect: UntypedFormControl;
	rolesList: any[] = [];

	userHasRights = signal(false);
	userIsValid = signal(false);

	currentOwner: Owner;
	currentResident: Resident;
	currentConsumptionInfoReceiver: ConsumptionInfoReceiver;

	currentUserIsOwner = false;
	currentUserIsPM = false;
	showManualDeliveryOptionSetting = signal(false);
	private realEstateSetting: RealEstateSetting;

	private currentArea: number;
	private currentRealEstateId: string;
	isMobile = false;

	readonly tooltip = viewChild<MatTooltip>('tooltip');

	constructor() {
		this.currentArea = Number(sessionStorage.getItem('selectedRealEstateArea') ?? this.sessionService.effectiveArea);
	}

	ngOnInit(): void {
		this.isMobile = this.layoutService.isMobile();
		sessionStorage.setItem('activeView', globalThis.location.href);

		this.activatedRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (params) => {
				const realEstateId = params.get('realEstateId');
				const tenantUnitId = params.get('tenantUnitId');
				if (!realEstateId || !tenantUnitId) {
					return;
				}
				const realEstateChanged = this.currentRealEstateId !== realEstateId;
				if (realEstateChanged) {
					this.currentRealEstateId = realEstateId;
				}
				this.loadTenantUnit(realEstateId, tenantUnitId, realEstateChanged);
			},
		});
	}

	ngAfterViewInit() {
		if (!sessionStorage.getItem('show_tenant_info')) {
			this.showTooltipInfo(15000);
			sessionStorage.setItem('show_tenant_info', 'false');
		}
	}

	showTooltipInfo(delay = 10000) {
		this.tooltip().show();
		setTimeout(() => this.tooltip().hide(delay));
	}

	openCustomSettings() {
		const dialogRef = this.dialog.open(TenantUnitDetailSettingsLegacyComponent, {
			autoFocus: false,
			restoreFocus: false,
			data: {
				currentTenantUnit: this.selectedTenantUnit(),
				currentRealEstate: this.selectedRealestate(),
			},
		});
		dialogRef.afterClosed().subscribe(() => {});
	}

	private setupTenantAndRealEstate() {
		if (this.selectedRealestate()?.id && this.selectedTenantUnit()?.id) {
			this.currentOwner = this.selectedTenantUnit().currentOwner;
			this.currentResident = this.selectedTenantUnit().currentResident;
			this.currentConsumptionInfoReceiver = this.selectedTenantUnit().currentConsumptionInfoReceiver;
			this.consumptionInfoReceiverService
				.getConsumptionInfoReceivers(this.currentArea, this.selectedRealestate().id, this.selectedTenantUnit().id)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((consumptionInfoReceivers) => {
					if (consumptionInfoReceivers.length > 0) {
						this.showConsumptionInfoReceiver.set(true);
					}
				});

			this.sessionService
				.getAllPermissionsOfLoggedInUser()
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((permissions) => {
					const permissionTypesMap = new Set(permissions.map((value) => value.permissionType));
					if (permissionTypesMap.has(PermissionTypeEnum.PROPERTY_MANAGEMENT)) {
						this.currentUserIsPM = true;
					} else if (permissionTypesMap.has(PermissionTypeEnum.OWNER)) {
						if (permissions.filter((value) => value.tenantUnitId === this.selectedTenantUnit().id).length) {
							this.currentUserIsOwner = true;
						}
					}
					this.energyEfficiencySettingService
						.getRealEstateSetting(this.sessionService.effectiveArea, this.selectedRealestate().id)
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe((realEstateSetting) => {
							this.realEstateSetting = realEstateSetting;
							this.showManualDeliveryOptionSetting.set(this.manualDeliveryOptionAllowedBySetting());
						});
				});
		}

		this.getRolesByCurrentUser();
		this.setSelector();
		this.setBreadCrumbs();
	}

	private loadTenantUnits(realEstateId: string) {
		this.tenantUnitServiceV1
			.getByRealEstateId(this.currentArea, realEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (tenantUnits) => {
					this.tenantUnits.set(this.sortTenantUnitsByNumber(tenantUnits) as never);
				},
				error: () => {
					this.tenantUnits.set([]);
				},
			});
	}

	private sortTenantUnitsByNumber(tenantUnits: TenantUnit[] | TenantUnitReference[]) {
		return [...tenantUnits].sort((a, b) => {
			return a.number === b.number ? a.subNumber - b.subNumber : a.number - b.number;
		});
	}

	private loadTenantUnit(realEstateId: string, tenantUnitId: string, reloadTenantUnits: boolean) {
		this.realEstateService
			.getRealEstate(this.currentArea, realEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (realEstate) => {
					this.selectedRealestate.set(realEstate);
					this.currentArea = realEstate.area;
					if (reloadTenantUnits) {
						this.loadTenantUnits(realEstateId);
					}
					this.tenantUnitService
						.getTenantUnitById(this.currentArea, realEstateId, tenantUnitId)
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe({
							next: (tenantUnit) => {
								this.selectedTenantUnit.set(tenantUnit);
								sessionStorage.setItem('selectedTenantUnitID', tenantUnit.id);
								this.loadBilling(realEstate);
								this.setupTenantAndRealEstate();
							},
						});
				},
			});
	}

	private loadBilling(realEstate: RealEstate) {
		this.billingService
			.getBillingsByRealEstate(realEstate.area, realEstate.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((billings: Billing[]) => {
				this.billingUnit.set(billings.at(0));
				if (this.billingUnit()?.portalActive) {
					this.loadTenantUnitReferences(billings.at(0)?.exportId);
				}
			});
	}

	private loadTenantUnitReferences(billingExportId: string) {
		this.tenantUnitReferenceService
			.readAllTenantUnitReference(this.sessionService.effectiveArea, billingExportId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					response.sort((a, b) => {
						return a.number === b.number ? a.subNumber - b.subNumber : a.number - b.number;
					});
					for (const TenantUnitReference of response) {
						TenantUnitReference.tenantUnitUserReferences?.sort((a, b) => {
							return dayjs(a.validTo).isAfter(dayjs(b.validTo), 'day') ? -1 : 1;
						});
					}
					this.tenantUnitReferences.set(response);
					this.selectedTenantUnitReference.set(
						response.find((tURD) => tURD.subNumber === this.selectedTenantUnit().subNumber && tURD.number === this.selectedTenantUnit().number)
					);
				},
				error: () => {
					this.snackBar.error(this.translocoService.translate('errors.loadingTenantUnitReferences'));
					this.tenantUnitReferences.set([]);
					this.selectedTenantUnitReference.set(undefined);
				},
			});
	}
	private manualDeliveryOptionAllowedBySetting() {
		return (
			(this.realEstateSetting?.pmAllowedManualDeliveryOption && this.currentUserIsPM) ||
			(this.realEstateSetting?.ownerAllowedManualDeliveryOption && this.currentUserIsOwner)
		);
	}

	private getRolesByCurrentUser() {
		// Read the roles according to user
		if (this.sessionService.isPropertyManagement() || this.sessionService.isDevRole() || this.sessionService.isSupportRole()) {
			this.showOwner.set(true);
			this.showConsumptionInfoReceiver.set(true);
			this.showResident.set(true);
			this.userHasRights.set(true);
			this.userIsValid.set(true);
		} else {
			this.setVisibilityFlagsByPermission();
		}
	}

	private async setVisibilityFlagsByPermission() {
		this.sessionService
			.getAllPermissionsOfLoggedInUser()
			// this.sessionService.getAllPermissionsOfLoggedInUser()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (permissionObjects) => {
					permissionObjects = permissionObjects.filter((value) => value.tenantUnitId === this.selectedTenantUnit()?.id);
					if (permissionObjects.length > 1) {
						const permissionTypes = new Set(permissionObjects.map((value) => value.permissionType));
						if (permissionTypes.has(PermissionTypeEnum.RESIDENT)) {
							this.showResident.set(true);
							this.userHasRights.set(true);
							this.sessionService.selectedTenantUnitRolePermission = permissionObjects.find(
								(value) => value.permissionType === PermissionTypeEnum.RESIDENT
							);
						}
						if (permissionTypes.has(PermissionTypeEnum.OWNER)) {
							this.sessionService.selectedTenantUnitRolePermission = permissionObjects.find(
								(value) => value.permissionType === PermissionTypeEnum.OWNER
							);
						}
						if (permissionTypes.has(PermissionTypeEnum.PROPERTY_MANAGEMENT)) {
							this.showResident.set(true);
							this.showOwner.set(true);
							this.showConsumptionInfoReceiver.set(true);
							this.userHasRights.set(true);
							this.sessionService.selectedTenantUnitRolePermission = permissionObjects.find(
								(value) => value.permissionType === PermissionTypeEnum.PROPERTY_MANAGEMENT
							);
						}
					} else {
						const permissionObject = permissionObjects[0];
						this.sessionService.selectedTenantUnitRolePermission = permissionObject;
						const permissionType = permissionObject?.permissionType;
						if (permissionType === PermissionObject.PermissionTypeEnum.OWNER) {
							this.showResident.set(true);
							this.showOwner.set(false);
							this.showConsumptionInfoReceiver.set(false);
							this.showTenant.set(false);
							this.userHasRights.set(true);
							const permissionValidUntilDate = dayjs(permissionObject.codeValidUntil);
							this.ownerService
								.getOwner(permissionObject.area, this.selectedRealestate().id, permissionObject.tenantUnitId, permissionObject.permissionUserId)
								.subscribe((owner) => {
									const ownerValidUntilDate = dayjs(owner.validTo);
									this.userIsValid.set(!permissionValidUntilDate.isBefore(dayjs(), 'day') && !ownerValidUntilDate.isBefore(dayjs(), 'day'));
								});
						} else if (permissionType === PermissionObject.PermissionTypeEnum.RESIDENT) {
							this.showResident.set(false);
							this.showOwner.set(true);
							this.showConsumptionInfoReceiver.set(true);
							this.userHasRights.set(true);
						} else if (
							permissionType === PermissionObject.PermissionTypeEnum.PROPERTY_MANAGEMENT ||
							this.currentOwner.id === this.currentResident.id
						) {
							this.showOwner.set(true);
							this.showConsumptionInfoReceiver.set(true);
							this.showResident.set(true);
							this.rolesList.push(
								{
									value: 'owner',
									viewValue: this.translocoService.translate('tenantunitDetails.owner'),
								},
								{
									value: 'resident',
									viewValue: this.translocoService.translate('tenantunitDetails.resident'),
								}
							);
							this.selected.push('owner', 'resident');
							this.userHasRights.set(true);
						} else if (!this.userHasRights()) {
							this.showResident.set(false);
							this.showOwner.set(false);
							this.showConsumptionInfoReceiver.set(false);
							this.showTenant.set(false);
							// unknown role, should not happen -> route to permissions
							this.snackBar.error(this.translocoService.translate('tenantunitDetails.noPermissionsFound'));

							this.router.navigate(['/permissions']);
						}
					}
					this.setSelector();
				},
				error: () => {
					this.snackBar.error(this.translocoService.translate('errors.permissionsLoading'));
				},
			});
	}

	private setSelector() {
		this.rolesSelect = new UntypedFormControl(this.selected);
	}

	private setBreadCrumbs() {
		if (this.router.url.includes('/tenantunits/')) {
			this.breadCrumbService.reset();
			this.breadCrumbService.addBreadCrumb({
				translationKey: 'realestates.realestates',
				icon: this.icons.get('realEstate'),
			});
			this.breadCrumbService.addBreadCrumb({
				translationKey: 'sideboard.tenantunits',
				icon: this.icons.get('tenantunit'),
			});
			this.breadCrumbService.addBreadCrumb({
				translationKey: 'sideboard.tenantunitDetails',
				icon: this.icons.get('people'),
			});
		}
	}
}
